import { cn } from 'cn';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
interface ConfirmationDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback when the dialog is closed (cancel or backdrop click) */
  onClose: () => void;
  /** Callback when the confirm button is clicked */
  onConfirm: () => void;
  /** Whether confirmation closes the dialog immediately @default true */
  closeOnConfirm?: boolean;
  /** Dialog title displayed in the header */
  title: string;
  /** Dialog description/message displayed in the body */
  description: React.ReactNode;
  /** Label for the confirm button @default 'Confirm' */
  confirmLabel?: string;
  /** Label for the cancel button @default 'Cancel' */
  cancelLabel?: string;
  /** Whether the dialog buttons should be disabled @default false */
  isSubmitting?: boolean;
  /**
   * Disables the confirm button only, without the `isSubmitting` loading
   * spinner and without disabling Cancel. For a caller that must block
   * confirmation on a dependency that hasn't resolved yet (e.g. an
   * org default still loading) — that isn't "submitting" and the user
   * must still be able to back out. @default false
   */
  confirmDisabled?: boolean;
  /** Variant for the confirm button @default 'primary' */
  confirmVariant?: 'primary' | 'destructive';
  /** Theme for the dialog surface; omit for the light default */
  theme?: 'dark';
  /**
   * Extra classes merged onto the dialog surface, after the default width
   * class — lets one caller widen its own instance (e.g. a longer
   * description) without shifting every other confirmation dialog built on
   * this component.
   */
  contentClassName?: string;
  /**
   * Extra body content rendered under the description, inside the same
   * scroll area. For controls that belong to the decision (an opt-out
   * checkbox, say) — `description` is prose and should stay non-interactive.
   */
  children?: React.ReactNode;
}

/**
 * A reusable confirmation dialog for destructive or important actions.
 *
 * The dark theme carries its own text/border classes: the base
 * dialog's `secondary-*`/`tertiary-*` tokens do not flip with
 * `data-theme`, so dark surfaces restate them in the
 * white/opacity idiom the everything-bar uses.
 *
 * @example
 * ```tsx
 * <ConfirmationDialog
 *   isOpen={showDialog}
 *   onClose={() => setShowDialog(false)}
 *   onConfirm={handleDelete}
 *   title="Delete Item"
 *   description="Are you sure you want to delete this item?"
 *   confirmLabel="Delete"
 * />
 * ```
 */
export function ConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  closeOnConfirm = true,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  isSubmitting = false,
  confirmVariant = 'primary',
  theme,
  confirmDisabled = false,
  contentClassName,
  children
}: ConfirmationDialogProps) {
  const dark = theme === 'dark';
  const handleConfirm = () => {
    onConfirm();
    // Async callers can retain control until their mutation settles.
    if (closeOnConfirm) onClose();
  };
  return <Dialog open={isOpen} onOpenChange={open => !open && onClose()} data-sentry-element="Dialog" data-sentry-component="ConfirmationDialog" data-sentry-source-file="confirmation-dialog.tsx">
      <DialogContent data-theme={theme} className={cn('sm:max-w-[320px]', dark && 'border-white/10', contentClassName)} data-sentry-element="DialogContent" data-sentry-source-file="confirmation-dialog.tsx">
        <DialogHeader className={cn(dark && 'border-white/[0.08]')} data-sentry-element="DialogHeader" data-sentry-source-file="confirmation-dialog.tsx">
          <DialogTitle className={cn(dark && 'text-white')} data-sentry-element="DialogTitle" data-sentry-source-file="confirmation-dialog.tsx">
            {title}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className={cn('pb-4', dark ? 'text-white/70' : 'text-secondary-700')} data-sentry-element="DialogBody" data-sentry-source-file="confirmation-dialog.tsx">
          {description}
          {children}
        </DialogBody>
        <DialogFooter data-sentry-element="DialogFooter" data-sentry-source-file="confirmation-dialog.tsx">
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting} size="lg" className={cn('flex-1', dark && 'border-white/15 bg-transparent text-white/80 hover:bg-white/[0.06] hover:text-white')} data-sentry-element="Button" data-sentry-source-file="confirmation-dialog.tsx">
            {cancelLabel}
          </Button>
          <Button variant={confirmVariant} onClick={handleConfirm} loading={isSubmitting} disabled={confirmDisabled} size="lg" className="flex-1" data-sentry-element="Button" data-sentry-source-file="confirmation-dialog.tsx">
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
}